/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as XLSX from 'xlsx';
import { PLATFORM_LIST, SOURCE_CHOICE_BULK_UPLOAD } from "../../../config/constants";

import {
  getHeaderRow,
  ImportError,
  readWorkbook,
  validateColumns,
  validateContent,
  validateDates,
  validateEntriesCount,
  validateMediaCounts,
  validatePlatforms,
  validateMediaFilesUrls,
  validatePostUploadType,
  preparePostDataAndUploadMedia,
  validateGoogleCTA,
  validatePostType
} from "./helpers";
import { LomavisCreatorConfig } from "../lomavisCreator/lomavisCreatorConfig";
import axiosClient from "../../../utils/axios";
import type { AppDispatch } from "../../store"; // adjust path as needed
import { creatorInitialState } from "../lomavisCreator/lomavisCreator";
import { add } from "lodash";
// Types
export interface Error{
  error: string;
  platform: string;
  rowIndex: number;
}
export interface RowStatus {
  index: number; // Original 0-based index from the sheet_to_json array
  originalRowNumber: number; // 1-based index for user display (index + 1, or maybe +2 if header exists)
  platforms: string[]; // Platforms targeted by this row
  status: 'Success' | 'Failed' | 'Cancelled' | 'Pending';
  error?: any;
  responseData?: any; // Optional: Store response data on success
}
interface BulkPostsImportState {
  emptyPostState: any,
  isLoading: boolean;
  uploadingStarted: boolean;
  uploadDone: boolean;
  totalPosts: number;
  errorsList: Error[];
  uploadedPostsCount: number;
  cancelled: boolean;
  controller: AbortController;
  rowStatuses: RowStatus[];
  ui: { showImportModal: boolean };
}

// Initial state
const initialState: BulkPostsImportState = {
  emptyPostState: null,
  isLoading: false,
  uploadingStarted: false,
  uploadDone: false,
  totalPosts: 0,
  uploadedPostsCount: 0,
  errorsList: [],
  ui: { showImportModal: false },
  cancelled: false,
  controller: new AbortController(),
  rowStatuses: [],
};

// Redux slice
const slice = createSlice({
  name: "bulkPostsImport",
  initialState,
  reducers: {
    startLoading: state => { state.isLoading = true },
    stopLoading: state => { state.isLoading = false },
    startUploading: (state, action: PayloadAction<number>) => {
      state.uploadingStarted = true;
      state.totalPosts = action.payload;
      state.uploadedPostsCount = 0;
    },
    updateRowStatus: (state, action: PayloadAction<Partial<RowStatus> & { index: number }>) => {
      const { index, ...updateData } = action.payload;
      const rowIndex = state.rowStatuses.findIndex(rs => rs.index === index);
      if (rowIndex !== -1) {
        // Merge new data into existing status object
        state.rowStatuses[rowIndex] = { ...state.rowStatuses[rowIndex], ...updateData };

        // Optionally update counts based on status
        if (updateData.status === 'Success') {
            state.uploadedPostsCount += 1; // Increment successful count
        } 
      }
    },

    // Action to mark remaining rows as Cancelled/Skipped
    markRemainingRowsAsCancelled: (state, action: PayloadAction<{ startIndex: number }>) => {
        for (let i = action.payload.startIndex; i < state.rowStatuses.length; i++) {
            if (state.rowStatuses[i].status === 'Pending') {
                 state.rowStatuses[i].status = 'Cancelled';
            }
        }
        state.cancelled = true; // Ensure cancelled flag is set
    },
    setBulkImportUiAttribute: (
      state,
      action: PayloadAction<{ attribute: keyof BulkPostsImportState['ui']; value: boolean }>
    ) => {
      state.ui[action.payload.attribute] = action.payload.value;
    },
    setEmptyPostState: (state, action: PayloadAction<any>) => {
      state.emptyPostState = JSON.parse(JSON.stringify(action.payload)); // Deep copy to avoid reference issues
    },
    setErrorsList: (state, action: PayloadAction<Error[]>) => {
      state.errorsList = action.payload;
    },
    clearErrorsList: state => {
      state.errorsList = [];
    },
    setCancelled: (state, action: PayloadAction<boolean>) => {
      state.cancelled = action.payload;
    },
    stopUploading: state => {
      state.uploadingStarted = false;
      state.uploadDone = false;
      state.cancelled = false;
      state.controller.abort();
    },
    setUploadDone: state => {
      state.uploadDone = true;
    },
    resetUploadState: state => {
      state.uploadingStarted = false;
      state.uploadDone = false;
      state.uploadedPostsCount = 0;
      state.errorsList = [];
      state.cancelled = false;
      state.controller = new AbortController();
    },
    initializeRowStatuses: (state, action: PayloadAction<any[]>) => {
      state.rowStatuses = action.payload.map((row, index) => ({
        index: index,
        originalRowNumber: index + 2, // Assuming header row + 1-based indexing
        platforms: [], // Placeholder, will be populated in the thunk
        status: 'Pending',
      }));
      state.totalPosts = action.payload.length; // Set totalPosts here too
      state.uploadedPostsCount = 0;
      state.errorsList = []; // Clear previous errors
      state.cancelled = false;
      state.uploadDone = false;
      state.uploadingStarted = true; // Mark as started
       // Reset controller only when starting a new upload fully
      if (state.controller.signal.aborted) {
          state.controller = new AbortController();
      }
    },
  },
});

export default slice.reducer;
export const {
  startLoading,
  stopLoading,
  startUploading,
  setBulkImportUiAttribute,
  setEmptyPostState,
  setErrorsList,
  setCancelled,
  updateRowStatus,
  resetUploadState,
  stopUploading,
  setUploadDone,
  markRemainingRowsAsCancelled,
  initializeRowStatuses

} = slice.actions;

// Main handler
export const handleFileUpload = (
  file: File,
  locations: any,
  onSuccess: (rows: any[]) => void,
  onError: (errorCode: any) => void,
) => async (dispatch: AppDispatch, getState: () => any): Promise<void> => {
    dispatch(resetUploadState());
    dispatch(startLoading()); // Initialize row statuses to empty
  try {
    const lomavisCreator = JSON.parse(JSON.stringify(creatorInitialState))
    const isMultipost = lomavisCreator.is_multipost;
    const activePlatforms = PLATFORM_LIST.filter(p => lomavisCreator.platformEnabled[p]);

    const wb = await readWorkbook(file);
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const header = getHeaderRow(sheet);
    validateColumns(header);

    const rows = XLSX.utils.sheet_to_json<any>(sheet);
    let errors: Error[] = [];
    
    errors = errors.concat(validateEntriesCount(rows));
    errors = errors.concat(validateDates(rows, header));
    errors = errors.concat(validatePlatforms(activePlatforms, rows, header));
    errors = errors.concat(validateContent(rows, header));
    errors = errors.concat(validateMediaCounts(rows, header));
    errors = errors.concat(validateMediaFilesUrls(rows, header));
    errors = errors.concat(validatePostUploadType(rows, header, isMultipost));
    errors = errors.concat(validateGoogleCTA(rows, header));
    errors = errors.concat(validatePostType(rows, header));
    
    console.log(`File ${file.name} validation completed with ${errors.length} errors`);
    console.log(errors);

    if (errors.length > 0) {
      dispatch(setErrorsList(errors));
      dispatch(stopLoading());
      onError(errors);
      return;
    }

    // Dispatch upload start
    dispatch(initializeRowStatuses(rows));
    dispatch(startUploading(rows.length));

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      // check cancelled
      if (getState().bulkPostsImport.controller.signal.aborted) {
        dispatch(markRemainingRowsAsCancelled({ startIndex: i }));
        dispatch(setUploadDone());
        
        break;
      }


      try {
        const postData = await preparePostDataAndUploadMedia(row, header, lomavisCreator, locations,i);
        postData.source = SOURCE_CHOICE_BULK_UPLOAD;
        let response;
          response = await axiosClient.post(
            lomavisCreator.lomavisCreatorConfig.http_endpoint,
            postData
          );
        console.log("Post uploaded successfully:", response.data);

        dispatch(updateRowStatus({
          index: i,
          status: 'Success',
          platforms: row[header[0]].split(',').map((p: string) => p.trim())
        }));
      } catch (err) {
        console.error("Error uploading post:", err);
        if (err.type=="validationError") {
          console.log("got validation error");
          dispatch(updateRowStatus({
            index: i,
            status: 'Failed',
            error: err.content,
            platforms: row[header[0]].split(',').map((p: string) => p.trim())
          }));
        }
        onError(err);
      }
    }

    dispatch(stopLoading());
    dispatch(setUploadDone());
    onSuccess(rows);
  } catch (err) {
    console.error(err);
    onError(err);
  }
}
